import { Flex, Heading } from '@radix-ui/themes';
import { useTranslation } from 'react-i18next';

import useEditBoardSettings from './useEditBoardSettings';
import NavigationGuard from '../../../shared/NavigationGuard';
import Tooltip from '../../../shared/Tooltip/Tooltip.jsx';
import CustomColorInput from './CustomizationSection/CustomColor.jsx';
import UnsavedChangesModal from '../../../shared/UnsavedChangesModal';
import { Button as FormButton } from '../../../shared/FormStyles';
import FileInput from '../../../shared/FileInput';
import TextInput from '../../../shared/FormStyles/TextInput.jsx';
import SectionCard from '../../../shared/SectionCard/SectionCard.jsx';
import { MomentsBanner } from './BannerSection/MomentsBanner.jsx';
import styles from '../BoardSetup.module.css';

const Branding = () => {
  const {
    handleChange,
    values,
    initialValues,
    handleSubmit,
    setFieldValue,
    inputError,
    handleBlur,
    showUnsavedChangesModal,
    setShowUnsavedChangesModal,
    setPendingAction,
    handleCloseModal,
    handleDiscardChanges,
    handleSaveAndLeave,
  } = useEditBoardSettings();
  const { t } = useTranslation(['shared', 'boardSetup']);

  // Check if branding fields are dirty
  const isBrandingDirty =
    values.name !== initialValues.name ||
    values.logo.name !== initialValues.logo.name ||
    values.primaryColor !== initialValues.primaryColor;

  // Check if banner fields are dirty
  const isBannerDirty =
    values.momentsHeader !== initialValues.momentsHeader ||
    values.momentsMessage !== initialValues.momentsMessage;

  // Combined dirty state for navigation guard
  const isDirty = isBrandingDirty || isBannerDirty;

  // Reset only branding fields
  const resetBrandingForm = () => {
    setFieldValue('name', initialValues.name);
    setFieldValue('logo', initialValues.logo);
    setFieldValue('primaryColor', initialValues.primaryColor);
  };

  // Reset only banner fields
  const resetBannerForm = () => {
    setFieldValue('momentsHeader', initialValues.momentsHeader);
    setFieldValue('momentsMessage', initialValues.momentsMessage);
  };

  const handleFileInput = e => {
    e.preventDefault();
    setFieldValue('logo', e.currentTarget.files[0]);
  };

  return (
    <NavigationGuard
      hasUnsavedChanges={isDirty}
      showUnsavedChangesModal={showUnsavedChangesModal}
      setShowUnsavedChangesModal={setShowUnsavedChangesModal}
      setPendingAction={setPendingAction}
    >
      <Flex gap="4">
        <Tooltip />
        <SectionCard
          className={styles.sectionCardContainer}
          headerClassName={styles.sectionCardHeader}
          title={t('boardSetup:customization.title')}
        >
          <Flex direction="column" gap="4" py="4" height="100%">
            <TextInput
              label={t('boardSetup:formLabel.name')}
              dataTip={t('boardSetup:customization.boardNameDataTip')}
              dataOffset="{ 'right': 150 }"
              id="demo_board_name"
              name="name"
              onChange={handleChange}
              value={values.name}
              error={inputError('name')}
              onBlur={handleBlur}
            />
            <FileInput
              id="logo"
              name="logo"
              fileTypes="image/jpg,image/jpeg,image/png"
              fileName={values.logo.name}
              onChange={handleFileInput}
              errorMessage={inputError('logo')}
              onBlur={handleBlur}
              label={t('boardSetup:formLabel.logo')}
              dataTip={t('boardSetup:customization.boardLogoDataTip')}
              dataOffset="{ 'right': 165 }"
            />
            <CustomColorInput
              setFieldValue={setFieldValue}
              primaryColor={values.primaryColor}
            />
            <Flex justify="end" gap="3" mt="auto">
              <FormButton variant="tertiary" onClick={resetBrandingForm}>
                {t('shared:cancel')}
              </FormButton>
              <FormButton
                ml="8px"
                variant="containedBlue"
                disabled={!isBrandingDirty}
                onClick={handleSubmit}
                type="submit"
              >
                {t('shared:save')}
              </FormButton>
            </Flex>
          </Flex>
        </SectionCard>
        {values.newAirbo && (
          <SectionCard
            className={styles.sectionCardContainer}
            headerClassName={styles.sectionCardHeader}
            headerContent={
              <Flex align="center" gap="1">
                <Heading size="4" m="0" p="0">
                  {t('boardSetup:moments.title')}
                </Heading>
                <i
                  className={`fa-regular fa-circle-info fa-xs ${styles.infoIcon}`}
                  data-tip={t('boardSetup:moments.titleDataTip')}
                  data-testid="moments-tooltip"
                />
              </Flex>
            }
          >
            <Flex direction="column" gap="4" py="4" height="100%">
              <MomentsBanner
                handleChange={handleChange}
                header={values.momentsHeader}
                welcomeMessage={values.momentsMessage}
              />
              <Flex justify="end" gap="3" mt="auto">
                <FormButton variant="tertiary" onClick={resetBannerForm}>
                  {t('shared:cancel')}
                </FormButton>
                <FormButton
                  ml="8px"
                  variant="containedBlue"
                  disabled={!isBannerDirty}
                  onClick={handleSubmit}
                  type="submit"
                >
                  {t('shared:save')}
                </FormButton>
              </Flex>
            </Flex>
          </SectionCard>
        )}
      </Flex>
      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        onClose={handleCloseModal}
        onDiscard={handleDiscardChanges}
        onSave={handleSaveAndLeave}
      />
    </NavigationGuard>
  );
};

export default Branding;
